import { connectAuthEmulator, getAuth, type Auth } from 'firebase/auth';
import { connectFirestoreEmulator, getFirestore, type Firestore } from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions, type Functions } from 'firebase/functions';
import { initializeApp, type FirebaseApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';

import { firebasePlatformFixture } from '../../testUtils/fixtures/evocContracts';

type RawEnvironment = {
  [key: string]: string | undefined;
};

export type FirebaseMode = 'emulator' | 'remote';
export type FirebaseAppEnvironment = 'local' | 'staging' | 'production';

export interface FirebaseClientConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
}

export interface FirebaseEnvironment {
  mode: FirebaseMode;
  appEnvironment: FirebaseAppEnvironment;
  firebaseConfig: FirebaseClientConfig;
  app: FirebaseApp;
  auth: Auth;
  db: Firestore;
  functions: Functions;
}

const ALLOWED_MODES: FirebaseMode[] = ['emulator', 'remote'];
const ALLOWED_ENVIRONMENTS: FirebaseAppEnvironment[] = ['local', 'staging', 'production'];

const isPresent = (value: string | undefined): value is string =>
  typeof value === 'string' && value.trim().length > 0;

const withLocalEmulatorDefaults = (env: RawEnvironment): RawEnvironment => {
  const requestedMode = env.VITE_FIREBASE_MODE?.trim();
  if (requestedMode && requestedMode !== 'emulator') {
    return env;
  }

  const localProfile = firebasePlatformFixture.profiles.local;
  const defaults: RawEnvironment = {
    VITE_FIREBASE_MODE: 'emulator',
    VITE_APP_ENVIRONMENT: 'local',
    VITE_FIREBASE_API_KEY: 'demo-evoc-local-api-key',
    VITE_FIREBASE_AUTH_DOMAIN: `${localProfile.projectId}.firebaseapp.com`,
    VITE_FIREBASE_PROJECT_ID: localProfile.projectId,
    VITE_FIREBASE_STORAGE_BUCKET: localProfile.storageBucket,
    VITE_FIREBASE_MESSAGING_SENDER_ID: '0',
    VITE_FIREBASE_APP_ID: 'demo-evoc-backoffice-local',
  };

  return Object.fromEntries(
    Object.entries({
      ...defaults,
      ...env,
    }).map(([key, value]) => [key, isPresent(value) ? value : defaults[key]])
  ) as RawEnvironment;
};

const getRequiredEnvValue = (env: RawEnvironment, key: string): string => {
  const value = env[key];
  if (!isPresent(value)) {
    throw new Error(`Missing required Firebase env variable: ${key}`);
  }

  return value;
};

const parseMode = (env: RawEnvironment): FirebaseMode => {
  const value = getRequiredEnvValue(env, 'VITE_FIREBASE_MODE');
  if (!ALLOWED_MODES.includes(value as FirebaseMode)) {
    throw new Error(
      `Invalid VITE_FIREBASE_MODE: ${value}. Expected one of: ${ALLOWED_MODES.join(', ')}`
    );
  }

  return value as FirebaseMode;
};

const parseAppEnvironment = (env: RawEnvironment): FirebaseAppEnvironment => {
  const value = getRequiredEnvValue(env, 'VITE_APP_ENVIRONMENT');
  if (!ALLOWED_ENVIRONMENTS.includes(value as FirebaseAppEnvironment)) {
    throw new Error(
      `Invalid VITE_APP_ENVIRONMENT: ${value}. Expected one of: ${ALLOWED_ENVIRONMENTS.join(', ')}`
    );
  }

  return value as FirebaseAppEnvironment;
};

const parseFirebaseConfig = (env: RawEnvironment, mode: FirebaseMode): FirebaseClientConfig => {
  const config = {
    apiKey: getRequiredEnvValue(env, 'VITE_FIREBASE_API_KEY'),
    authDomain: getRequiredEnvValue(env, 'VITE_FIREBASE_AUTH_DOMAIN'),
    projectId: getRequiredEnvValue(env, 'VITE_FIREBASE_PROJECT_ID'),
    storageBucket: getRequiredEnvValue(env, 'VITE_FIREBASE_STORAGE_BUCKET'),
    messagingSenderId: getRequiredEnvValue(env, 'VITE_FIREBASE_MESSAGING_SENDER_ID'),
    appId: getRequiredEnvValue(env, 'VITE_FIREBASE_APP_ID'),
    ...(env.VITE_FIREBASE_MEASUREMENT_ID
      ? { measurementId: env.VITE_FIREBASE_MEASUREMENT_ID }
      : {}),
  };

  const selectedProfile =
    mode === 'emulator'
      ? firebasePlatformFixture.profiles.local
      : firebasePlatformFixture.profiles.remote;

  if (config.projectId !== selectedProfile.projectId) {
    throw new Error(
      `Invalid Firebase projectId: ${config.projectId}. Expected ${selectedProfile.projectId}`
    );
  }

  if (config.storageBucket !== selectedProfile.storageBucket) {
    throw new Error(
      `Invalid Firebase storageBucket: ${config.storageBucket}. Expected ${selectedProfile.storageBucket}`
    );
  }

  return config;
};

const selectFirebasePlatformProfile = (mode: FirebaseMode) =>
  mode === 'emulator'
    ? firebasePlatformFixture.profiles.local
    : firebasePlatformFixture.profiles.remote;

const connectEmulators = (auth: Auth, db: Firestore, functions: Functions): void => {
  const emulators = firebasePlatformFixture.profiles.local.emulators;

  connectAuthEmulator(auth, `http://localhost:${emulators.auth.port}`);
  connectFunctionsEmulator(functions, 'localhost', emulators.functions.port);
  connectFirestoreEmulator(db, 'localhost', emulators.firestore.port);
};

const initializeRemoteAppCheck = (
  app: FirebaseApp,
  env: RawEnvironment,
  mode: FirebaseMode
): void => {
  if (mode !== 'remote') {
    return;
  }
  const siteKey = getRequiredEnvValue(env, 'VITE_FIREBASE_APP_CHECK_RECAPTCHA_ENTERPRISE_SITE_KEY');

  initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(siteKey),
    isTokenAutoRefreshEnabled: true,
  });
};

export const createFirebaseEnvironment = (
  env: RawEnvironment = import.meta.env
): FirebaseEnvironment => {
  const resolvedEnv = withLocalEmulatorDefaults(env);
  const mode = parseMode(resolvedEnv);
  const appEnvironment = parseAppEnvironment(resolvedEnv);
  const firebaseConfig = parseFirebaseConfig(resolvedEnv, mode);
  const selectedProfile = selectFirebasePlatformProfile(mode);

  const app = initializeApp(firebaseConfig);
  initializeRemoteAppCheck(app, resolvedEnv, mode);
  const auth = getAuth(app);
  const db = getFirestore(app);
  const functions = getFunctions(app, selectedProfile.functionsRegion);

  if (mode === 'emulator') {
    connectEmulators(auth, db, functions);
  }

  return {
    mode,
    appEnvironment,
    firebaseConfig,
    app,
    auth,
    db,
    functions,
  };
};
